import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  Divider,
  Paper,
  InputAdornment,
  useTheme
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LabelIcon from '@mui/icons-material/Label';
import { v4 as uuidv4 } from 'uuid';
import { getKeywords, saveKeywords } from '../../utils/storage';

const KeywordView = ({ onSearch, onNavItemClick }) => {
  const [keywords, setKeywords] = useState([]);
  const theme = useTheme();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingKeyword, setEditingKeyword] = useState(null);
  const [keywordName, setKeywordName] = useState('');
  const [keywordQuery, setKeywordQuery] = useState('');
  const [keywordColor, setKeywordColor] = useState(theme.palette.primary.main);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [keywordToDelete, setKeywordToDelete] = useState(null);

  // キーワードデータの読み込み
  useEffect(() => {
    const loadedKeywords = getKeywords();
    setKeywords(loadedKeywords);
  }, []);

  // キーワードの保存
  const saveKeywordData = (newKeywords) => {
    saveKeywords(newKeywords);
    setKeywords(newKeywords);
  };

  // キーワード追加ダイアログを開く
  const handleAddKeyword = () => {
    setEditingKeyword(null);
    setKeywordName('');
    setKeywordQuery('');
    setKeywordColor(theme.palette.primary.main);
    setDialogOpen(true);
  };

  // キーワード編集ダイアログを開く
  const handleEditKeyword = (keyword) => {
    setEditingKeyword(keyword);
    setKeywordName(keyword.name);
    setKeywordQuery(keyword.query);
    setKeywordColor(keyword.color || theme.palette.primary.main);
    setDialogOpen(true);
  };

  // キーワード削除ダイアログを開く
  const handleDeleteClick = (keyword) => {
    setKeywordToDelete(keyword);
    setDeleteDialogOpen(true);
  };

  // キーワードの削除を確定
  const handleDeleteConfirm = () => {
    if (keywordToDelete) {
      const newKeywords = keywords.filter(k => k.id !== keywordToDelete.id);
      saveKeywordData(newKeywords);
      setDeleteDialogOpen(false);
      setKeywordToDelete(null);
    }
  };

  // キーワードの保存（追加または更新）
  const handleSaveKeyword = () => {
    if (!keywordName.trim() || !keywordQuery.trim()) return;

    let newKeywords;
    const newKeyword = {
      id: editingKeyword ? editingKeyword.id : uuidv4(),
      name: keywordName.trim(),
      query: keywordQuery.trim(),
      color: keywordColor,
      createdAt: editingKeyword ? editingKeyword.createdAt : new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    if (editingKeyword) {
      // 既存のキーワードを更新
      newKeywords = keywords.map(k => k.id === editingKeyword.id ? newKeyword : k);
    } else {
      // 新しいキーワードを追加
      newKeywords = [...keywords, newKeyword];
    }

    saveKeywordData(newKeywords);
    setDialogOpen(false);
  };

  // キーワードをクリックして検索実行し、時系列ビューに切り替え
  const handleKeywordClick = (keyword) => {
    if (onSearch) {
      // 検索対象を時系列ビューに限定する
      onSearch(keyword.query, 'timeline');
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>保存済みキーワード</Typography>
      <Divider sx={{ mb: 3 }} />

      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddKeyword}
        >
          キーワードを追加
        </Button>
      </Box>

      {keywords.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="textSecondary">
            保存されたキーワードはありません。「キーワードを追加」ボタンから追加できます。
          </Typography>
        </Paper>
      ) : (
        <List component={Paper}>
          {keywords.map((keyword) => (
            <ListItem key={keyword.id} divider>
              <ListItemIcon>
                <LabelIcon sx={{ color: keyword.color || theme.palette.primary.main }} />
              </ListItemIcon>
              <ListItemText
                primary={keyword.name}
                secondary={`検索クエリ: ${keyword.query}`}
              />
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Chip
                  label="検索"
                  color="primary"
                  variant="outlined"
                  size="small"
                  onClick={() => handleKeywordClick(keyword)}
                  icon={<SearchIcon />}
                  sx={{ mr: 1 }}
                />
                <IconButton edge="end" onClick={() => handleEditKeyword(keyword)} sx={{ mr: 1 }}>
                  <EditIcon />
                </IconButton>
                <IconButton edge="end" onClick={() => handleDeleteClick(keyword)}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            </ListItem>
          ))}
        </List>
      )}

      {/* キーワード追加・編集ダイアログ */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>
          {editingKeyword ? 'キーワードを編集' : 'キーワードを追加'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            検索キーワードに名前を付けて保存します。後で簡単に検索できるようになります。
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="キーワード名"
            fullWidth
            value={keywordName}
            onChange={(e) => setKeywordName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="検索クエリ"
            fullWidth
            value={keywordQuery}
            onChange={(e) => setKeywordQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ mr: 2 }}>
              色:
            </Typography>
            <input
              type="color"
              value={keywordColor}
              onChange={(e) => setKeywordColor(e.target.value)}
              style={{ width: '40px', height: '40px' }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>キャンセル</Button>
          <Button onClick={handleSaveKeyword} color="primary" variant="contained">
            保存
          </Button>
        </DialogActions>
      </Dialog>

      {/* 削除確認ダイアログ */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>キーワードを削除</DialogTitle>
        <DialogContent>
          <DialogContentText>
            「{keywordToDelete?.name}」を削除してもよろしいですか？この操作は元に戻せません。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>キャンセル</Button>
          <Button onClick={handleDeleteConfirm} color="error">
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default KeywordView;
